import { supabase } from './supabaseClient';

// ========================================
// SUPABASE AUTH-BASED AUTHENTICATION
// ========================================
// This uses Supabase Auth for secure JWT-based authentication
// Profile data is still stored in students/teachers tables

// ========================================
// NETWORK ERROR DETECTION
// ========================================

/**
 * Helper to detect network errors
 */
function isNetworkError(error) {
  if (!error) return false;
  const errorMessage = error.message || error.toString();
  return (
    errorMessage.includes('Failed to fetch') ||
    errorMessage.includes('NetworkError') ||
    errorMessage.includes('ERR_INTERNET_DISCONNECTED') ||
    errorMessage.includes('Network request failed') ||
    errorMessage.includes('fetch failed')
  );
}

// STUDENT SIGN UP (Supabase Auth)
export async function signUpStudent({ full_name, email, password, phone, grade_level }) {
  try {
    // 1. Create Supabase Auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          role: 'student',
          full_name
        }
      }
    });

    if (authError) {
      console.error('Auth signup error:', authError);
      if (isNetworkError(authError)) {
        return { error: 'No internet connection. Please check your network and try again.' };
      }
      return { error: authError.message };
    }

    if (!authData.user) {
      return { error: 'Failed to create user account' };
    }

    // 2. Create profile in students table
    const { data: profileData, error: profileError } = await supabase
      .from('students')
      .insert([{
        id: authData.user.id, // Link to auth user
        full_name,
        email,
        phone,
        grade_level
      }])
      .select()
      .single();

    if (profileError) {
      console.error('Profile creation error:', profileError);
      if (isNetworkError(profileError)) {
        return { error: 'No internet connection. Please check your network and try again.' };
      }
      // Auth user was created but profile failed - cleanup may be needed
      return { error: 'Failed to create student profile' };
    }

    return {
      data: {
        user: authData.user,
        session: authData.session,
        profile: profileData
      }
    };
  } catch (error) {
    console.error('Student signup error:', error);
    if (isNetworkError(error)) {
      return { error: 'No internet connection. Please check your network and try again.' };
    }
    return { error: error.message };
  }
}

// STUDENT SIGN IN (Supabase Auth)
export async function signInStudent({ email, password }) {
  try {
    // 1. Sign in with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (authError) {
      console.error('Auth signin error:', authError);
      if (isNetworkError(authError)) {
        return { error: 'No internet connection. Please check your network and try again.' };
      }
      return { error: 'Invalid email or password' };
    }

    if (!authData.user) {
      return { error: 'Invalid email or password' };
    }

    // 2. Get profile from students table
    const { data: profileData, error: profileError } = await supabase
      .from('students')
      .select('*')
      .eq('id', authData.user.id)
      .single();

    if (profileError) {
      console.error('Profile fetch error:', profileError);
      if (isNetworkError(profileError)) {
        return { error: 'No internet connection. Please check your network and try again.' };
      }
      return { error: 'Student profile not found' };
    }

    return {
      data: {
        user: authData.user,
        session: authData.session,
        profile: profileData
      }
    };
  } catch (error) {
    console.error('Student signin error:', error);
    if (isNetworkError(error)) {
      return { error: 'No internet connection. Please check your network and try again.' };
    }
    return { error: 'Invalid email or password' };
  }
}

// TEACHER SIGN UP (Supabase Auth) - Custom Email Confirmation
export async function signUpTeacher({ full_name, email, password, phone, subjects, grade_levels, certificates, profile_photo, hourly_rate, gender }) {
  try {
    // 1. Create Supabase Auth user WITHOUT email confirmation
    // We'll handle email confirmation ourselves
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          role: 'teacher',
          full_name
        },
        // Disable Supabase email confirmation - we'll send our own
        emailRedirectTo: undefined
      }
    });

    if (authError) {
      console.error('Auth signup error:', authError);
      if (isNetworkError(authError)) {
        return { error: 'No internet connection. Please check your network and try again.' };
      }
      return { error: authError.message };
    }

    if (!authData.user) {
      return { error: 'Failed to create user account' };
    }

    // 2. Create profile in Teachers table
    const { data: profileData, error: profileError } = await supabase
      .from('Teachers')
      .insert([{
        id: authData.user.id, // Link to auth user
        full_name,
        email,
        phone,
        subjects,
        grade_levels,
        certificates,
        profile_photo,
        hourly_rate,
        gender
      }])
      .select()
      .single();

    if (profileError) {
      console.error('Profile creation error:', profileError);
      if (isNetworkError(profileError)) {
        return { error: 'No internet connection. Please check your network and try again.' };
      }
      return { error: 'Failed to create teacher profile' };
    }

    // No email sent - teacher will complete form on first sign-in
    return {
      data: {
        user: authData.user,
        session: authData.session,
        profile: profileData
      }
    };
  } catch (error) {
    console.error('Teacher signup error:', error);
    if (isNetworkError(error)) {
      return { error: 'No internet connection. Please check your network and try again.' };
    }
    return { error: error.message };
  }
}

// TEACHER SIGN IN (Supabase Auth)
export async function signInTeacher({ email, password }) {
  try {
    // 1. Sign in with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (authError) {
      console.error('Auth signin error:', authError);
      if (isNetworkError(authError)) {
        return { error: 'No internet connection. Please check your network and try again.' };
      }
      return { error: 'Invalid email or password' };
    }

    if (!authData.user) {
      return { error: 'Invalid email or password' };
    }

    // 2. Get profile from Teachers table
    const { data: profileData, error: profileError } = await supabase
      .from('Teachers')
      .select('*')
      .eq('id', authData.user.id)
      .single();

    if (profileError) {
      console.error('Profile fetch error:', profileError);
      if (isNetworkError(profileError)) {
        return { error: 'No internet connection. Please check your network and try again.' };
      }
      return { error: 'Teacher profile not found' };
    }

    return {
      data: {
        user: authData.user,
        session: authData.session,
        profile: profileData
      }
    };
  } catch (error) {
    console.error('Teacher signin error:', error);
    if (isNetworkError(error)) {
      return { error: 'No internet connection. Please check your network and try again.' };
    }
    return { error: 'Invalid email or password' };
  }
}

// ========================================
// SESSION MANAGEMENT UTILITIES
// ========================================

/**
 * Get current session and JWT token
 */
export async function getSession() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error && isNetworkError(error)) {
      return {
        session: null,
        error: {
          message: 'No internet connection. Please check your network and try again.',
          isNetworkError: true
        }
      };
    }

    return { session, error };
  } catch (error) {
    if (isNetworkError(error)) {
      return {
        session: null,
        error: {
          message: 'No internet connection. Please check your network and try again.',
          isNetworkError: true
        }
      };
    }
    return { session: null, error };
  }
}

/**
 * Get JWT access token for API calls
 * Returns null if no active session
 */
export async function getAccessToken() {
  const { session, error } = await getSession();
  if (error?.isNetworkError) {
    console.warn('Cannot get access token: Network disconnected');
    return null;
  }
  return session?.access_token || null;
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error && isNetworkError(error)) {
      return {
        user: null,
        error: {
          message: 'No internet connection. Please check your network and try again.',
          isNetworkError: true
        }
      };
    }

    return { user, error };
  } catch (error) {
    if (isNetworkError(error)) {
      return {
        user: null,
        error: {
          message: 'No internet connection. Please check your network and try again.',
          isNetworkError: true
        }
      };
    }
    return { user: null, error };
  }
}

/**
 * Sign out current user
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  return { error };
}

/**
 * Send password reset email
 */
export async function resetPassword(email) {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`
  });
  return { data, error };
}

/**
 * Update user password (after reset)
 */
export async function updatePassword(newPassword) {
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword
  });
  return { data, error };
}

/**
 * Helper to get Authorization header for API calls
 */
export async function getAuthHeaders() {
  const token = await getAccessToken();
  if (!token) {
    return {};
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

// ========================================
// GOOGLE OAUTH AUTHENTICATION
// ========================================

/**
 * Sign in with Google OAuth
 * @param {string} role - 'student' or 'teacher'
 * @returns {Promise} - Redirects to Google OAuth
 */
export async function signInWithGoogle(role = 'student') {
  try {
    // Store the role in localStorage so we can retrieve it after redirect
    localStorage.setItem('pending_oauth_role', role);
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/signin?oauth_callback=true`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
        scopes: 'email profile'
      }
    });

    if (error) {
      console.error('Google OAuth error:', error);
      if (isNetworkError(error)) {
        return { error: 'No internet connection. Please check your network and try again.' };
      }
      return { error: error.message };
    }

    return { data };
  } catch (error) {
    console.error('Google sign in error:', error);
    if (isNetworkError(error)) {
      return { error: 'No internet connection. Please check your network and try again.' };
    }
    return { error: error.message };
  }
}

/**
 * Handle Google OAuth callback - creates profile if needed
 * @param {string} role - 'student' or 'teacher'
 * @returns {Promise} - User data and profile
 */
export async function handleGoogleOAuthCallback(role) {
  try {
    // Get the current session (should be set after OAuth redirect)
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
      console.error('No session after OAuth:', sessionError);
      return { error: 'Authentication failed. Please try again.' };
    }

    const user = session.user;
    if (!user) {
      return { error: 'No user found in session' };
    }

    // Extract user info from Google provider
    const email = user.email;
    const fullName = user.user_metadata?.full_name || user.user_metadata?.name || '';
    const avatarUrl = user.user_metadata?.avatar_url || user.user_metadata?.picture || '';

    // Check if user already exists in the appropriate table
    const tableName = role === 'teacher' ? 'Teachers' : 'students';
    const idField = role === 'teacher' ? 'id' : 'uuid_id';

    // First, try to find by email
    const { data: existingByEmail, error: emailCheckError } = await supabase
      .from(tableName)
      .select('*')
      .eq('email', email)
      .single();

    if (existingByEmail && !emailCheckError) {
      // User already exists with this email - update the uuid if needed
      if (role === 'student' && existingByEmail.uuid_id !== user.id) {
        await supabase
          .from(tableName)
          .update({ uuid_id: user.id })
          .eq('email', email);
      } else if (role === 'teacher' && existingByEmail.id !== user.id) {
        // For teachers, we might need to link the account
        await supabase
          .from(tableName)
          .update({ id: user.id })
          .eq('email', email);
      }

      return {
        data: {
          user,
          session,
          profile: existingByEmail,
          isNewUser: false
        }
      };
    }

    // Check if profile exists by ID
    const { data: existingProfile, error: profileCheckError } = await supabase
      .from(tableName)
      .select('*')
      .eq(idField, user.id)
      .single();

    if (existingProfile && !profileCheckError) {
      // Profile already exists
      return {
        data: {
          user,
          session,
          profile: existingProfile,
          isNewUser: false
        }
      };
    }

    // Profile doesn't exist - create new profile
    const newProfile = {
      [idField]: user.id,
      full_name: fullName,
      email: email,
      profile_photo: avatarUrl,
      created_at: new Date().toISOString(),
      auth_provider: 'google'
    };

    if (role === 'teacher') {
      newProfile.confirmation_completed = false;
    }

    const { data: createdProfile, error: createError } = await supabase
      .from(tableName)
      .insert([newProfile])
      .select()
      .single();

    if (createError) {
      console.error('Profile creation error:', createError);
      // If profile creation fails due to duplicate, try to fetch existing
      if (createError.code === '23505') {
        const { data: existingProfile2 } = await supabase
          .from(tableName)
          .select('*')
          .eq('email', email)
          .single();
        
        if (existingProfile2) {
          return {
            data: {
              user,
              session,
              profile: existingProfile2,
              isNewUser: false
            }
          };
        }
      }
      return { error: 'Failed to create profile. Please try again.' };
    }

    return {
      data: {
        user,
        session,
        profile: createdProfile,
        isNewUser: true
      }
    };
  } catch (error) {
    console.error('OAuth callback error:', error);
    if (isNetworkError(error)) {
      return { error: 'No internet connection. Please check your network and try again.' };
    }
    return { error: error.message };
  }
}

/**
 * Check if there's a pending OAuth session and handle it
 * @returns {Promise} - Session info if OAuth callback pending
 */
export async function checkPendingOAuthSession() {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error || !session) {
      return { session: null, pending: false };
    }

    // Check if this is from Google OAuth
    const provider = session.user?.app_metadata?.provider;
    if (provider === 'google') {
      const pendingRole = localStorage.getItem('pending_oauth_role');
      return { 
        session, 
        pending: !!pendingRole,
        role: pendingRole || 'student'
      };
    }

    return { session: null, pending: false };
  } catch (error) {
    console.error('Check pending OAuth error:', error);
    return { session: null, pending: false };
  }
}
